import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckboxMultiSelect, { MultiSelectOption } from '@/components/ui/CheckboxMultiSelect/CheckboxMultiSelect';
import { FundClassification } from '@/models/common/FundClassification';
import { Role } from '@/models/common/Role';
import HoverHint from '@/components/ui/Tooltip/HoverHint';
import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';
import eyeIcon from '@/assets/icons/eye.svg';
import useNameMaps from './useNameMaps';
import { targetingLines } from './targetingNames';
import styles from './TargetingPicker.module.scss';

export type TargetKind = 'funds' | 'groups' | 'users' | 'classifications' | 'roles';
const KINDS: TargetKind[] = ['funds', 'groups', 'users', 'classifications', 'roles'];

export interface TargetingValue {
  fundIds: number[];
  groupIds: number[];
  userEmails: string[];
  classifications: FundClassification[];
  roles: Role[];
}

interface TargetingPickerProps {
  value: TargetingValue;
  onChange: (next: TargetingValue) => void;
  fundOptions: MultiSelectOption<number>[];
  groupOptions: MultiSelectOption<number>[];
  userOptions: MultiSelectOption<string>[];
  classificationOptions: MultiSelectOption<string>[];
  roleOptions: MultiSelectOption<string>[];
  onCreateGroup: () => void;
  /** The groups themselves, so a group's row can show who is in it on hover. */
  groups: FundGroupDtoIn[];
  /** Lists still downloading, by kind: the popover shows a spinner for them instead of «אין תוצאות». */
  loading?: Partial<Record<TargetKind, boolean>>;
}

/**
 * Whom a partial release reaches (HAT-1489 rework): what is picked, as tags on one line, and the five
 * lists behind «+ הוספה», opened in place. The lists are the same five as before; they only stop
 * taking the whole window when nothing in them is being chosen.
 */
/** Drawn at once; the search finds the rest. 4,211 checkboxes took the popover a long moment to open. */
const MAX_ROWS = 150;

const TargetingPicker = ({ value, onChange, fundOptions, groupOptions, userOptions, classificationOptions, roleOptions, onCreateGroup, groups, loading = {} }: TargetingPickerProps) => {
  const { t } = useTranslation();
  const { maps } = useNameMaps();
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  /** Who is in a group, on hover over the eye - not a second window over the dialog. */
  const groupMembers = (groupId: number) => {
    const group = groupById.get(groupId);
    if (!group) return null;
    return (
      <>
        <strong>{group.name}</strong>
        {group.description && <div className="muted">{group.description}</div>}
        {targetingLines({ fundIds: group.fundIds, userEmails: group.userEmails, groupIds: [], classifications: [], roles: [] }, maps, t).map((line) => (
          <div key={line}>{line}</div>
        ))}
      </>
    );
  };
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<TargetKind>('funds');

  const labelOf = useMemo(() => {
    const maps = {
      funds: new Map(fundOptions.map((o) => [String(o.value), o.label])),
      groups: new Map(groupOptions.map((o) => [String(o.value), `${o.label} (${o.sublabel ?? ''})`])),
      users: new Map(userOptions.map((o) => [String(o.value), o.label])),
      classifications: new Map(classificationOptions.map((o) => [String(o.value), o.label])),
      roles: new Map(roleOptions.map((o) => [String(o.value), o.label])),
    };
    return (k: TargetKind, v: string | number) => maps[k].get(String(v)) ?? String(v);
  }, [fundOptions, groupOptions, userOptions, classificationOptions, roleOptions]);

  const counts: Record<TargetKind, number> = {
    funds: value.fundIds.length,
    groups: value.groupIds.length,
    users: value.userEmails.length,
    classifications: value.classifications.length,
    roles: value.roles.length,
  };

  // Tags in the order the admin thinks of them: projects, then who else.
  const tags: { kind: TargetKind; key: string; label: string }[] = [
    ...value.fundIds.map((id) => ({ kind: 'funds' as const, key: String(id), label: labelOf('funds', id) })),
    ...value.groupIds.map((id) => ({ kind: 'groups' as const, key: String(id), label: t('featureFlags.dialog.picker.groupTag', { name: labelOf('groups', id) }) })),
    ...value.classifications.map((c) => ({ kind: 'classifications' as const, key: c, label: t('featureFlags.dialog.picker.classificationTag', { name: labelOf('classifications', c) }) })),
    ...value.userEmails.map((e) => ({ kind: 'users' as const, key: e, label: labelOf('users', e) })),
    ...value.roles.map((r) => ({ kind: 'roles' as const, key: r, label: t('featureFlags.dialog.picker.roleTag', { name: labelOf('roles', r) }) })),
  ];

  const remove = (tag: { kind: TargetKind; key: string }) => {
    const without = <T extends string | number>(list: T[]) => list.filter((v) => String(v) !== tag.key);
    onChange({
      ...value,
      fundIds: tag.kind === 'funds' ? without(value.fundIds) : value.fundIds,
      groupIds: tag.kind === 'groups' ? without(value.groupIds) : value.groupIds,
      userEmails: tag.kind === 'users' ? without(value.userEmails) : value.userEmails,
      classifications: tag.kind === 'classifications' ? without(value.classifications) : value.classifications,
      roles: tag.kind === 'roles' ? without(value.roles) : value.roles,
    });
  };

  const list = () => {
    switch (kind) {
      case 'funds':
        return <CheckboxMultiSelect options={fundOptions} selected={value.fundIds} onChange={(v) => onChange({ ...value, fundIds: v })} maxHeightClass="max-h-[296px]" searchPlaceholder="featureFlags.dialog.picker.search" maxRendered={MAX_ROWS} hideCount loading={loading[kind]} />;
      case 'groups':
        return (
          <>
            <CheckboxMultiSelect
              options={groupOptions}
              selected={value.groupIds}
              onChange={(v) => onChange({ ...value, groupIds: v })}
              maxHeightClass="max-h-[258px]"
              searchPlaceholder="featureFlags.dialog.picker.search"
              maxRendered={MAX_ROWS}
              hideCount
              loading={loading.groups}
              renderRowEnd={(o) => (
                <HoverHint hint={groupMembers(o.value)} className="inline-flex opacity-60 hover:opacity-100">
                  <img src={eyeIcon} alt={t('view')} className="w-4 h-4" />
                </HoverHint>
              )}
            />
            <button type="button" onClick={onCreateGroup} className={`${styles.link} body-xs-semibold`}>
              + {t('fundGroups.addGroup')}
            </button>
          </>
        );
      case 'users':
        return <CheckboxMultiSelect options={userOptions} selected={value.userEmails} onChange={(v) => onChange({ ...value, userEmails: v })} maxHeightClass="max-h-[296px]" searchPlaceholder="featureFlags.dialog.picker.search" maxRendered={MAX_ROWS} hideCount loading={loading[kind]} />;
      case 'classifications':
        return <CheckboxMultiSelect options={classificationOptions} selected={value.classifications} onChange={(v) => onChange({ ...value, classifications: v as FundClassification[] })} searchable={false} maxHeightClass="max-h-[296px]" searchPlaceholder="featureFlags.dialog.picker.search" maxRendered={MAX_ROWS} hideCount loading={loading[kind]} />;
      case 'roles':
      default:
        return <CheckboxMultiSelect options={roleOptions} selected={value.roles} onChange={(v) => onChange({ ...value, roles: v as Role[] })} searchable={false} maxHeightClass="max-h-[296px]" searchPlaceholder="featureFlags.dialog.picker.search" maxRendered={MAX_ROWS} hideCount loading={loading[kind]} />;
    }
  };

  const summary = [
    value.fundIds.length > 0 && t('featureFlags.summary.funds', { count: value.fundIds.length }),
    value.groupIds.length > 0 && t('featureFlags.summary.groups', { count: value.groupIds.length }),
    value.classifications.length > 0 && t('featureFlags.summary.classifications', { count: value.classifications.length }),
    value.userEmails.length > 0 && t('featureFlags.summary.users', { count: value.userEmails.length }),
    value.roles.length > 0 && t('featureFlags.summary.roles', { count: value.roles.length }),
  ].filter(Boolean).join(' · ');

  /*
   * The lists open IN the release, where the tags were - not in a popover. A popover hung off the
   * row, moved when the dialog scrolled, and on a short screen opened half above it (Chaya 25.10).
   * While it is open the tags wait: a tag added on every tick changed the row's height and moved
   * the list under the cursor. The panel is one height for every kind, so switching tabs moves nothing.
   */
  if (open) {
    return (
      <div className={styles.panel} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}>
        <div className={styles.panelHead}>
          <span className={`${styles.label} body-sm-regular`}>{t('featureFlags.dialog.picker.openTo')}</span>
          <div className={styles.tabs} role="tablist">
            {KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={kind === k}
                className={`${styles.tab} ${kind === k ? styles.tabActive : ''} body-sm-semibold`}
                onClick={() => setKind(k)}
              >
                {t(`featureFlags.dialog.picker.kinds.${k}`)}
                {counts[k] > 0 && <span className={styles.count}>({counts[k]})</span>}
              </button>
            ))}
          </div>
        </div>
        {/* Keyed by kind: each list starts with its own search and its own order. */}
        <div key={kind} className={styles.list}>{list()}</div>
        <div className={styles.foot}>
          <button type="button" className={`${styles.done} body-sm-semibold`} onClick={() => setOpen(false)}>{t('featureFlags.dialog.picker.done')}</button>
          <span className="body-sm-regular text-(--font-body-2)">{summary || t('featureFlags.summary.none')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <span className={`${styles.label} body-sm-regular`}>{t('featureFlags.dialog.picker.openTo')}</span>
      {tags.map((tag) => (
        <span key={`${tag.kind}-${tag.key}`} className={`${styles.tag} body-sm-regular`}>
          {tag.label}
          <button type="button" className={styles.remove} onClick={() => remove(tag)} aria-label={t('featureFlags.dialog.picker.remove', { name: tag.label })}>
            ×
          </button>
        </span>
      ))}
      <button type="button" className={`${styles.add} body-sm-semibold`} onClick={() => setOpen(true)}>
        + {t('featureFlags.dialog.picker.add')}
      </button>
    </div>
  );
};

export default TargetingPicker;
